/**
 * Checkout.
 *
 * Against Polar this screen does very little on purpose: it states what is about
 * to be bought, what happens when it renews, and how to get out — then hands the
 * browser to Polar, who take the card. Nothing here touches a card number, and
 * there is deliberately no code that could.
 *
 * The mock path is still here and still says so at full volume. It exists so the
 * flow can be worked on without a payment provider, and a fake purchase that
 * looks real would be the first thing in this codebase to break the rule the
 * product is sold on.
 *
 * `CheckoutCompleteView` is where Polar sends the customer back. It is separate
 * because the money and the plan arrive at different times: Polar confirms the
 * payment to the browser, and the webhook that actually grants the plan lands a
 * moment later. Telling somebody their plan is active before it is would be a
 * small lie with a support ticket attached.
 */

import { useEffect, useState } from 'react';
import { api, errorMessage, type Account, type CheckoutMode } from '../api';
import { PLANS, type Plan, type PlanId } from '../../core/plans';
import { PERIOD_MS } from '../../core/subscription';
import { link, navigate } from '../router';
import { Alert, Button, Spinner, formatDate } from '../ui';
import { ArrowLeftIcon, CheckIcon, CircleCheckIcon, Icon, IconHover, Wordmark } from '../icons';
import { MERCHANT_OF_RECORD } from '../../core/company';
import { Confetti } from './Confetti';

function Shell({ children, showBack }: { children: React.ReactNode; showBack?: boolean }) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-4">
          <a {...link('/evidence')} className="flex items-center gap-2 font-medium tracking-tight">
            <span className="text-accent">
              <Wordmark size={20} />
            </span>
            Vouchkeep
          </a>
          {showBack ? (
            <IconHover as="a" className="btn btn-ghost" {...link('/pricing')}>
              <Icon icon={ArrowLeftIcon} size={15} />
              <span className="text-[13px]">All plans</span>
            </IconHover>
          ) : null}
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-5 py-14">{children}</main>
    </div>
  );
}

function MockBanner() {
  return (
    <div className="mb-6 rounded-lg border border-line bg-warn-wash px-4 py-3">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 shrink-0 text-warn">
          <Icon icon={CircleCheckIcon} size={15} isAnimated={false} />
        </span>
        <div className="text-[13.5px] leading-relaxed text-warn">
          <b className="font-medium">Nothing is charged.</b> This deployment is running the mock
          provider, so confirming below simply switches the plan on. Everything except the money is
          real: the period, the renewal date, the cancellation, and the receipt in your inbox.
        </div>
      </div>
    </div>
  );
}

export function CheckoutView({
  plan: planId,
  account,
  mode,
  onUpdated,
}: {
  plan: PlanId;
  account: Account;
  mode: CheckoutMode;
  onUpdated: (next: Account) => void;
}) {
  const plan = PLANS[planId];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Account | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.checkout(planId as Exclude<PlanId, 'free'>);

      // Polar. Leave the app entirely — and do not clear `busy` first, because
      // the navigation is already on its way and a button that springs back to
      // life invites a second click and a second checkout session.
      if (result.url) {
        window.location.href = result.url;
        return;
      }

      if (result.account) {
        onUpdated(result.account);
        setDone(result.account);
      }
      setBusy(false);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  if (mode === 'off') {
    return (
      <Shell showBack>
        <h1 className="text-[26px] leading-tight">Not for sale yet</h1>
        <p className="mt-3 max-w-lg text-[14px] leading-relaxed text-muted">
          Checkout is not configured on this deployment, so there is no way to pay for{' '}
          {plan.name} right now. The Free plan is unaffected and everything you have stays where it
          is.
        </p>
        <div className="mt-7">
          <a {...link('/pricing')}>
            <Button variant="secondary">Back to plans</Button>
          </a>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="card p-8 text-center">
          <span className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-accent-wash text-accent">
            <Icon icon={CircleCheckIcon} size={20} isAnimated={false} />
          </span>
          <h1 className="text-[22px]">{plan.name} is active</h1>
          <p className="mx-auto mt-2 max-w-md text-[13.5px] leading-relaxed text-muted">
            Your limits are raised now. The period runs until{' '}
            <b className="text-ink">{formatDate(done.planUntil ?? Date.now() + PERIOD_MS)}</b> and
            renews on its own from there — you can turn that off in Settings whenever you like, and
            cancelling keeps the plan until the end of what you have already paid for.
          </p>

          <div className="mt-7 flex flex-wrap justify-center gap-2">
            <a {...link('/evidence')}>
              <Button variant="primary">Back to the evidence bank</Button>
            </a>
            <a {...link('/settings')}>
              <Button variant="secondary">Manage subscription</Button>
            </a>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell showBack>
      <h1 className="text-[26px] leading-tight">Confirm the {plan.name} plan</h1>
      <p className="mt-2 max-w-lg text-[14px] leading-relaxed text-muted">{plan.tagline}</p>

      <div className="mt-8">
        {mode === 'mock' ? <MockBanner /> : null}

        <div className="card overflow-hidden">
          <div className="flex items-baseline justify-between gap-4 border-b border-line px-6 py-5">
            <div>
              <div className="text-[15px] font-medium">{plan.name}</div>
              <div className="mt-0.5 text-[12.5px] text-faint">
                Monthly, renewing. Cancel any time.
              </div>
            </div>
            <div className="text-right">
              <div className="text-[26px] font-medium tracking-tight">${plan.price}</div>
              <div className="text-[12.5px] text-faint">per month</div>
            </div>
          </div>

          <ul className="space-y-2.5 px-6 py-5">
            {plan.features.map((feature) => (
              <li key={feature} className="flex items-start gap-2 text-[13.5px] leading-relaxed">
                <span className="mt-0.5 shrink-0 text-accent">
                  <Icon icon={CheckIcon} size={14} isAnimated={false} />
                </span>
                <span className="text-muted">{feature}</span>
              </li>
            ))}
          </ul>

          <div className="space-y-1 border-t border-line bg-sunken px-6 py-4 text-[13px] text-muted">
            <div className="flex justify-between">
              <span>Billed to</span>
              <span className="text-ink">{account.email}</span>
            </div>
            <div className="flex justify-between">
              <span>First renewal</span>
              <span className="text-ink">{formatDate(Date.now() + PERIOD_MS)}</span>
            </div>
            <div className="flex justify-between">
              <span>Charged today</span>
              <span className="text-ink">
                {mode === 'mock' ? '$0.00 — nothing is connected' : `$${plan.price}.00`}
              </span>
            </div>
          </div>
        </div>

        {error ? (
          <div className="mt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        <Button
          variant="primary"
          className="mt-6 w-full"
          busy={busy}
          onClick={() => void confirm()}
        >
          {mode === 'mock' ? `Switch to ${plan.name}` : `Continue to payment`}
        </Button>

        <button
          type="button"
          className="mt-3 w-full text-[13px] text-faint hover:text-muted"
          onClick={() => navigate('/pricing')}
        >
          Not now
        </button>

        {mode === 'polar' ? (
          <p className="mt-6 text-center text-[12px] leading-relaxed text-faint">
            Payment is taken by {MERCHANT_OF_RECORD.name}, our {MERCHANT_OF_RECORD.role}, who issue
            your invoice and handle any sales tax. Your card details never reach Vouchkeep. By
            continuing you agree to <a {...link('/terms')} className="underline">the terms</a> and{' '}
            <a {...link('/refunds')} className="underline">the refund policy</a> — cancel any time,
            and a full refund within 14 days of your first payment.
          </p>
        ) : null}
      </div>
    </Shell>
  );
}

/* -------------------------------------------------------------------------- */
/* the return from Polar                                                      */
/* -------------------------------------------------------------------------- */

/** How long to keep asking before saying "it is on its way" and stepping aside. */
const ACTIVATION_TIMEOUT_MS = 20_000;
const POLL_INTERVAL_MS = 1_500;

/**
 * Where Polar sends the customer back.
 *
 * The payment is already done by the time this renders; the plan is not, because
 * it is granted by a webhook travelling on its own schedule. So this screen waits
 * — and when the wait runs long it says exactly that, rather than reporting a
 * failure. A customer who has just been charged and is told "something went
 * wrong" writes to support; one who is told "paid, activating, nothing else for
 * you to do" does not.
 */
export function CheckoutCompleteView({ onActivated }: { onActivated: () => void }) {
  const [active, setActive] = useState<{ plan: Plan; renewsAt: number | null } | null>(null);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    const poll = async () => {
      if (cancelled) return;
      try {
        const me = await api.me();
        if (cancelled) return;
        if (me.account.plan !== 'free') {
          setActive({ plan: me.plan, renewsAt: me.account.planUntil });
          onActivated();
          return;
        }
      } catch {
        // Keep waiting. A failed poll here is nothing to report: the payment is
        // Polar's business and it has already succeeded.
      }
      if (Date.now() - startedAt > ACTIVATION_TIMEOUT_MS) {
        setSlow(true);
        return;
      }
      window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
    };

    void poll();
    return () => {
      cancelled = true;
    };
  }, [onActivated]);

  return (
    <Shell>
      <div className="card p-8 text-center">
        {active ? (
          <>
            <Confetti />
            <span className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-accent-wash text-accent">
              <Icon icon={CircleCheckIcon} size={20} isAnimated={false} />
            </span>
            <h1 className="text-[22px]">You are on {active.plan.name}</h1>
            <p className="mx-auto mt-2 max-w-md text-[13.5px] leading-relaxed text-muted">
              {active.plan.tagline}
            </p>

            {/* What they just bought, in the words the pricing page used. Read
                from core/plans.ts, so this cannot advertise a limit the product
                does not enforce. */}
            <ul className="mx-auto mt-6 max-w-sm space-y-2.5 text-left">
              {active.plan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-[13.5px] leading-relaxed">
                  <span className="mt-0.5 shrink-0 text-accent">
                    <Icon icon={CheckIcon} size={14} isAnimated={false} />
                  </span>
                  <span className="text-muted">{feature}</span>
                </li>
              ))}
            </ul>

            {/* The three questions somebody asks right after paying, answered
                before they have to go looking. Leaving them to the receipt is
                how a new customer's first act becomes an email to support. */}
            <div className="mt-7 space-y-1.5 border-t border-line pt-5 text-[12.5px] text-faint">
              {active.renewsAt !== null ? (
                <p>
                  Renews on <span className="text-muted">{formatDate(active.renewsAt)}</span>.
                  Cancelling any time before then keeps the plan to the end of the period.
                </p>
              ) : null}
              <p>
                Your receipt comes from {MERCHANT_OF_RECORD.name}, and the charge appears as{' '}
                <span className="text-muted">{MERCHANT_OF_RECORD.statementHint}</span>.
              </p>
              <p>
                Changed your mind? Full refund within 14 days —{' '}
                <a {...link('/refunds')} className="underline underline-offset-2 hover:text-muted">
                  no questions
                </a>
                .
              </p>
            </div>

            <div className="mt-7 flex flex-wrap justify-center gap-2">
              <a {...link('/evidence')}>
                <Button variant="primary">Back to the evidence bank</Button>
              </a>
              <a {...link('/settings')}>
                <Button variant="secondary">Manage subscription</Button>
              </a>
            </div>
          </>
        ) : slow ? (
          <>
            <h1 className="text-[22px]">Payment received</h1>
            <p className="mx-auto mt-2 max-w-md text-[13.5px] leading-relaxed text-muted">
              Your plan is taking a little longer than usual to switch over. Nothing is wrong and
              there is nothing for you to do — it lands on its own, usually within a minute or two.
              Refresh Settings to check, and if it has not moved in an hour, write to us and we will
              sort it out.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-2">
              <a {...link('/settings')}>
                <Button variant="primary">Open Settings</Button>
              </a>
              <a {...link('/contact')}>
                <Button variant="secondary">Contact support</Button>
              </a>
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center">
              <Spinner size={20} />
            </div>
            <h1 className="text-[22px]">Activating your plan</h1>
            <p className="mx-auto mt-2 max-w-md text-[13.5px] leading-relaxed text-muted">
              Payment went through. We are waiting for {MERCHANT_OF_RECORD.name} to confirm it,
              which normally takes a few seconds. You can leave this page — the plan switches over
              either way.
            </p>
          </>
        )}
      </div>
    </Shell>
  );
}
